import type { ReplacementCart } from '@iheartjane/cart/utils';
import {
  getGroupSpecialSelection,
  getLocalTags,
  cartStorageKey,
  resetBagPickupMode,
  setReplacementCart,
  toReplacementCart,
} from '@iheartjane/cart/utils';
import type { UserSegmentIds } from '@iheartjane/data-access';
import {
  EventNames,
  trackBranchCheckout,
  trackError,
} from '@iheartjane/tracking';
import type { AppMode, PriceId, ReservationMode } from '@iheartjane/types';
import { Storage, shopperTimeZone } from '@iheartjane/utils';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import isEmpty from 'lodash-es/isEmpty';
import pick from 'lodash-es/pick';
import type { NavigateFunction } from 'react-router';
import type { DeepReadonly } from 'ts-essentials';

import { trackBranchIoError } from '@jane/dm/internal';
import {
  trackCartEvent,
  trackCheckoutSuccess,
} from '@jane/monolith/customer/cart';
import type { CustomerThunkAction } from '@jane/monolith/customer/customerAction';
import type {
  AppDispatch,
  configureCustomerStore,
} from '@jane/monolith/customer/redux';
import { openModal } from '@jane/monolith/customer/redux/modal';
import { NotificationsService } from '@jane/monolith/services/notifications';
import type {
  CheckoutRequest,
  CheckoutResponse,
} from '@jane/monolith/sources/cart';
import { CartSource } from '@jane/monolith/sources/cart';
import { CartProductsSource } from '@jane/monolith/sources/cartProducts';
import {
  applyCrmRedemption as applyCrmRedemptionSource,
  removeCrmRedemption as removeCrmRedemptionSource,
} from '@jane/monolith/sources/crmIntegration';
import type {
  AddedOrUpdatedProductInCart,
  MoeEvent,
  UserPreferencesType,
} from '@jane/shared-ecomm/tracking';
import { moeAddToBag, moeCheckout, track } from '@jane/shared-ecomm/tracking';
import {
  postAnalyticsEvent,
  postCheckoutAnalyticsEvent,
} from '@jane/shared-ecomm/util';
import type { CheckoutErrorDetails } from '@jane/shared/data-access';
import { checkoutErrorDetails } from '@jane/shared/data-access';
// eslint-disable-next-line no-restricted-imports
import type {
  Cart,
  MenuProduct,
  PendingCartProduct,
  Store,
  StoreSpecial,
} from '@jane/shared/models';
import type {
  Address,
  AlmostGone,
  BundlePossibilities,
  CartLineItems,
  CrmProviderType,
  CrmRedemption,
} from '@jane/shared/types';
import {
  formatTime,
  getActualUnitPrice,
  monolithPaths as paths,
  prepareCartProduct,
} from '@jane/shared/util';

import {
  getBundlePossibilities,
  setTouchedProduct,
} from './bundlePossibilities';
import { getPosCart } from './posCart';

interface HeadlessCartProduct {
  count: number;
  price_id: PriceId;
  product_id: number;
}

const isEmptyCart = (cart: DeepReadonly<Cart>) =>
  isEmpty(cart) || cart.uuid === '' || cart.products?.length === 0;

export const getCart =
  (forceRemote = false): CustomerThunkAction =>
  (dispatch, getState, extraArgsRef) => {
    const appMode = extraArgsRef?.current?.appMode;
    const {
      embeddedApp: { partnerId },
    } = getState();

    dispatch(initiateGetCart());
    const getError = (err: unknown) => dispatch(getCartError(err));
    const getSuccess = ({ cart }: { cart: Cart }) =>
      dispatch(getLocalCartSuccess(cart, forceRemote));

    CartSource.getLocal(appMode, partnerId).then(getSuccess, getError);
  };

export const getRemoteCart =
  (uuid: string, onSuccess?: () => void): CustomerThunkAction =>
  (dispatch, getState) => {
    const { cart: cartState } = getState();
    const getError = (err: unknown) => dispatch(getCartError(err));
    const getRemoteCartSuccess = (result: { cart: Cart }) => {
      dispatch(getCartSuccess(result));
      dispatch(getRemoteCartSucceeded());
      onSuccess && onSuccess();
    };

    CartSource.getRemote(
      uuid,
      cartState.deliveryAddress,
      cartState.selectedPaymentMethod
    ).then(getRemoteCartSuccess, getError);
  };

const cartExpired = (cartState: CartState) => {
  const twoSecondsAgo = Date.now() - 2000;
  return (
    !cartState.wasLoadedAt ||
    twoSecondsAgo > new Date(cartState.wasLoadedAt).getTime()
  );
};

export const getLocalCartSuccess =
  (cart: Cart, forceRemote = false): CustomerThunkAction =>
  (dispatch, getState) => {
    dispatch(initiateGetLocalCartSuccess());
    const { cart: cartState } = getState();
    const getError = (err: unknown) => dispatch(getCartError(err));
    const getSuccess = (result: { cart: Cart }) => {
      dispatch(getCartSuccess(result));
      dispatch(getRemoteCartSucceeded());
    };

    if (isEmptyCart(cart)) {
      return dispatch(getCartSuccess({ cart }));
    }

    if (cartExpired(cartState) || forceRemote) {
      CartSource.getRemote(
        cart.uuid,
        cartState.deliveryAddress,
        cartState.selectedPaymentMethod
      ).then(getSuccess, getError);
    } else {
      dispatch(getCartSuccess({ cart: cartState.cart }));
    }
  };

export const getCartSuccess =
  (result: { cart: DeepReadonly<Cart> }): CustomerThunkAction =>
  (dispatch, getState, extraArgsRef) => {
    const { bundlePossibilities } = getState();

    const isHeadless = !!extraArgsRef?.current?.headlessStoreId;

    dispatchGetCartSuccessAction(
      getCartSucceeded,
      result,
      bundlePossibilities,
      dispatch,
      isHeadless
    );
  };

export const refreshCart =
  (cart: DeepReadonly<Cart>): CustomerThunkAction =>
  (dispatch, getState) => {
    const { cart: cartState } = getState();
    const getError = (err: unknown) => dispatch(getCartError(err));
    const getSuccess = (result: { cart: Cart }) => {
      dispatch(refreshCartSuccess(result));
    };

    CartSource.getRemote(
      cart.uuid,
      cartState.deliveryAddress,
      cartState.selectedPaymentMethod
    ).then(getSuccess, getError);
  };

const dispatchGetCartSuccessAction = (
  actionCreator: typeof getCartSucceeded | typeof getUserCartSucceeded,
  result: { cart: DeepReadonly<Cart> },
  bundlePossibilities: {
    bundlePossibilities: BundlePossibilities;
    filterDiscountableProducts: boolean;
    showingBundlePossibilities: boolean;
    touchedProductId?: string | number;
  },
  dispatch: AppDispatch,
  isHeadless = false
) => {
  const { cart } = result;
  dispatch(actionCreator(result));
  if (isEmptyCart(cart) && !isHeadless) {
    return dispatch(clearCart());
  }

  if (
    bundlePossibilities.touchedProductId &&
    bundlePossibilities.showingBundlePossibilities
  ) {
    dispatch(getBundlePossibilities(cart.uuid));
  }
  return;
};

const getUserCartSuccess =
  (result: { cart: Cart }): CustomerThunkAction =>
  (dispatch, getState) => {
    const { bundlePossibilities } = getState();

    dispatchGetCartSuccessAction(
      getUserCartSucceeded,
      result,
      bundlePossibilities,
      dispatch
    );
  };

export const getCartForUser =
  (): CustomerThunkAction => (dispatch, getState, extraArgsRef) => {
    const {
      cart: {
        cart: { uuid },
      },
      embeddedApp: { partnerId },
    } = getState();

    dispatch(initiateGetCart());

    const getSuccess = ({ cart }: { cart: Cart }) =>
      dispatch(getUserCartSuccess({ cart }));
    const getError = (err: unknown) => dispatch(getCartError(err));

    const storeId = extraArgsRef?.current?.headlessStoreId ?? partnerId;
    const appMode = extraArgsRef?.current?.appMode;

    CartSource.getForUser(appMode, storeId, uuid).then(getSuccess, getError);
  };

// checks local storage & clears any existing cart. used in app modes like BrandEmbed where old carts aren't supported
export const resetCart =
  (): CustomerThunkAction => (dispatch, getState, extraArgsRef) => {
    const appMode = extraArgsRef?.current?.appMode;
    const {
      embeddedApp: { partnerId },
    } = getState();

    dispatch(initiateGetCart());

    const getSuccess = ({ cart }: { cart: Cart }) => {
      dispatch(getCartSuccess({ cart }));

      dispatch(clearCart());
    };
    const getError = (err: unknown) => dispatch(getCartError(err));

    CartSource.getLocal(appMode, partnerId).then(getSuccess, getError);
  };

export const clearCart =
  (preserveRemoteCart?: boolean): CustomerThunkAction<Promise<unknown>> =>
  (dispatch, getState, extraArgsRef) => {
    const appMode = extraArgsRef?.current?.appMode;
    const {
      cart: { cart },
      embeddedApp: { partnerId },
    } = getState();

    dispatch(initiateClearCart());

    if (isEmptyCart(cart)) return Promise.resolve();

    const clearCartSuccessHandler = () => {
      Storage.remove('cart');
      Storage.remove(cartStorageKey(appMode, partnerId));
    };

    const clearCartErrorHandler = () => dispatch(clearCartError());

    const promise = preserveRemoteCart
      ? Promise.resolve()
      : CartSource.delete(cart.uuid);

    return promise.then(clearCartSuccessHandler, clearCartErrorHandler);
  };

export const deleteCartItem =
  (productId: string | number, priceId: PriceId): CustomerThunkAction =>
  (dispatch, getState) => {
    const {
      bundlePossibilities,
      cart: cartState,
      store: storeState,
    } = getState();
    const { cart } = cartState;

    const product = cart.products.find(
      (product) => product.id === productId && product.price_id === priceId
    );

    const store = storeState?.store;
    const storeCurrency = store?.currency_code;

    if (product) {
      const { name, category, brand, count } = product;
      const pricePerUnit = getActualUnitPrice(product, product.price_id) || 0;
      track({
        event: EventNames.RemovedProductFromCart,
        productBrand: brand,
        productCategory: category,
        productId: productId.toString(),
        productName: name,
        productPrice: pricePerUnit,
        quantity: count,
        storeCurrency,
      });
    }

    if (bundlePossibilities.showingBundlePossibilities) {
      dispatch(setTouchedProduct(undefined));
    }

    const basePrice = product ? Number(product[`price_${priceId}`]) : 0;
    const discountedPrice = product
      ? Number(product[`discounted_price_${priceId}`])
      : 0;

    CartProductsSource.delete(productId, priceId, cart.uuid)
      .then(() => {
        dispatch(cartDeleteItemSuccess({ priceId, productId }));

        // only dispatch events after we are sure the request was successful
        postAnalyticsEvent({
          name: 'cartItemRemoval',
          properties: {
            priceId,
            productId,
            ...(basePrice > 0 && { price: basePrice }),
            ...(discountedPrice > 0 && { discountedPrice }),
          },
        });

        if (cart.products.length === 1) {
          postAnalyticsEvent({
            name: 'cartEmptied',
          });
        }

        // do not re-fetch cart if last product was just removed; cart will already have been deleted
        if (cart.products.length > 1) {
          dispatch(
            analyzeSpecials({
              promoCode: cart.promo_code,
              reservationMode: cart.reservation_mode,
              groupSpecialId: getGroupSpecialSelection(cart.uuid) ?? undefined,
            })
          );
          // we also need to refresh the pos cart calculation
          dispatch(getPosCart());
        }
      })
      .catch((err) => {
        const action = matchError(err);
        dispatch(action);
      });
  };

export const removeSpecialHasChangedFlag =
  (): CustomerThunkAction => (dispatch, getState) => {
    const { cart: cartState } = getState();
    dispatch(initiateRemoveSpecialHasChangedFlag());
    CartSource.removeSpecialHasChangedFlag(cartState.cart.uuid).then(() =>
      dispatch(removeSpecialHasChangedFlagSuccess())
    );
  };

export const deleteUnavailableProducts =
  (productIds: (string | number)[]): CustomerThunkAction =>
  (dispatch, getState) => {
    const {
      cart: { cart },
    } = getState();

    if (!cart.uuid) {
      return;
    }

    dispatch(initiateDeleteUnavailableProducts());

    CartProductsSource.deleteUnavailableProducts(cart.uuid).then(() => {
      dispatch(deleteUnavailableProductsSuccess(productIds));
    });
  };

/** v1 opened the drawer after an add in every app mode but these two. */
export const opensDrawerAfterAdd = (
  appMode: AppMode | undefined,
  withoutDrawer?: boolean
) => appMode !== 'headless' && appMode !== 'bloom' && !withoutDrawer;

export const replaceCart =
  (
    { product, store }: ReplacementCart,
    moeTrack: (event: MoeEvent) => void,
    drawer: { close: () => void; open: () => void }
  ): CustomerThunkAction<Promise<unknown>> =>
  (dispatch, getState, extraArgsRef) => {
    const appMode = extraArgsRef?.current?.appMode;
    drawer.close();

    return dispatch(clearCart())
      .then(() => {
        const { application, cart, embeddedApp } = getState();

        return CartProductsSource.add({
          appMode,
          appModeName: appMode,
          cart_uuid: cart.cart.uuid,
          cartProduct: {
            count: product.count,
            id: product.product_id,
            price_id: product.price_id,
            recommended: product.recommended,
          },
          janeDeviceId: application.janeDeviceId!,
          store,
          tags: getLocalTags(appMode, embeddedApp.partnerId),
        });
      })
      .then((result) => {
        moeTrack(
          moeAddToBag({
            cartUuid: result.cart_uuid,
            product,
            storeId: store.id,
            storeName: store.name,
          })
        );

        if (getState().bundlePossibilities.showingBundlePossibilities) {
          dispatch(setTouchedProduct(product.product_id));
        }

        dispatch(
          getRemoteCart(result.cart_uuid, () => {
            dispatch(getPosCart());
            if (opensDrawerAfterAdd(appMode)) drawer.open();
          })
        );
      })
      .catch((err) => {
        dispatch(matchError(err));
      });
  };

export const removeCartUser =
  (): CustomerThunkAction => (dispatch, getState) => {
    const {
      cart: { cart, deliveryAddress },
    } = getState();
    if (cart.uuid === '') {
      return;
    }

    CartSource.update({
      delivery_address: deliveryAddress,
      user_id: null,
      uuid: cart.uuid,
    }).then((result) => {
      dispatch(getCartSuccess(result));
    });
  };

export const updateReservationMode =
  ({
    reservationMode,
  }: {
    reservationMode: ReservationMode;
  }): CustomerThunkAction =>
  (dispatch, getState) => {
    const {
      cart: { cart, deliveryAddress },
    } = getState();
    if (!cart.uuid) return;

    dispatch(cartUpdateReservationMode({ reservationMode }));

    CartSource.update({
      delivery_address: deliveryAddress,
      reservation_mode: reservationMode,
      user_group_special_id: getGroupSpecialSelection(cart.uuid) ?? undefined,
      uuid: cart.uuid,
    }).then((result) => {
      dispatch(getCartSucceeded(result));

      // we also need to refresh the pos cart calculation
      dispatch(getPosCart());
    });
  };

export const updateDeliveryAddress =
  ({ deliveryAddress }: { deliveryAddress: Address }): CustomerThunkAction =>
  (dispatch, getState) => {
    const {
      cart: { cart, selectedPaymentMethod },
    } = getState();

    if ((deliveryAddress.street?.length || 0) > 0) {
      dispatch(initiateUpdateDeliveryAddress({ deliveryAddress }));

      if (cart.uuid) {
        CartSource.getRemote(
          cart.uuid,
          deliveryAddress,
          selectedPaymentMethod
        ).then((result) => {
          dispatch(getCartSucceeded(result));

          // we also need to refresh the pos cart calculation
          dispatch(getPosCart());
        });
      }
    }
  };

export const updateHeadlessDeliveryAddress =
  ({ deliveryAddress }: { deliveryAddress: Address }): CustomerThunkAction =>
  (dispatch) => {
    if ((deliveryAddress.street?.length || 0) > 0) {
      dispatch(initiateUpdateDeliveryAddress({ deliveryAddress }));
    }
  };

export const analyzeSpecials =
  ({
    groupSpecialId,
    reservationMode,
    promoCode,
  }: {
    groupSpecialId: string | number | undefined;
    promoCode: string | undefined;
    reservationMode: ReservationMode;
  }): CustomerThunkAction =>
  (dispatch, getState) => {
    const {
      bundlePossibilities,
      cart: { cart, deliveryAddress },
    } = getState();

    if (isEmptyCart(cart)) {
      return;
    }

    dispatch(initiateAnalyzeSpecials());

    return CartSource.optimizeSpecials({
      delivery_address: deliveryAddress,
      promo_code: promoCode,
      reservation_mode: reservationMode,
      user_group_special_id: groupSpecialId,
      uuid: cart.uuid,
    }).then(
      ({ cart }) => {
        dispatch(analyzeSpecialsSuccess({ cart }));

        if (
          bundlePossibilities.touchedProductId &&
          bundlePossibilities.showingBundlePossibilities
        ) {
          dispatch(getBundlePossibilities(cart.uuid));
        }
      },
      (err) => dispatch(getCartError(err))
    );
  };

export interface CrmParams extends CrmRedemption {
  crm_member_points: number;
}

export const CRM_REDEMPTION_ERROR_MESSAGE =
  "We couldn't complete that request. Please refresh and try again.";

export const applyCrmRedemption =
  (crmParams: CrmParams): CustomerThunkAction =>
  (dispatch, getState) => {
    const {
      cart: { cart, deliveryAddress },
    } = getState();

    dispatch(initiateApplyCrmRedemption());

    const handleFailure = ({
      error,
      errorMessage,
      notify = true,
    }: { error?: unknown; errorMessage?: string; notify?: boolean } = {}) => {
      dispatch(applyCrmRedemptionError());

      if (notify) {
        NotificationsService.error(
          errorMessage || CRM_REDEMPTION_ERROR_MESSAGE
        );
      }

      if (!errorMessage) {
        trackError(
          error instanceof Error
            ? error
            : new Error('Failed to apply CRM redemption'),
          {
            cartUuid: cart.uuid,
            crmProvider: crmParams.crm_provider,
            crmRewardId: crmParams.crm_reward_id,
            rejection: error instanceof Error ? undefined : error,
          }
        );
      }
    };

    applyCrmRedemptionSource(
      {
        ...crmParams,
        cart_uuid: cart.uuid,
      },
      deliveryAddress
    ).then(
      (result) => {
        if (!result?.cart) {
          handleFailure({ errorMessage: result?.error_message });
          return;
        }

        dispatch(updateCrmRedemptions({ cart: result.cart }));
      },
      (error) => handleFailure({ error, notify: error instanceof Error })
    );
  };

export const removeCrmRedemption =
  (id: string | number, crmProvider: CrmProviderType): CustomerThunkAction =>
  (dispatch, getState) => {
    const {
      cart: { cart },
    } = getState();

    removeCrmRedemptionSource(id, crmProvider).then(() => {
      dispatch(removeCrmRedemptionSuccess({ id }));
      dispatch(
        analyzeSpecials({
          promoCode: cart.promo_code,
          reservationMode: cart.reservation_mode,
          groupSpecialId: getGroupSpecialSelection(cart.uuid) ?? undefined,
        })
      );
    });
  };

const handleChangedCartError = (err: unknown, dispatch: AppDispatch) => {
  if (typeof err !== 'string') {
    // Every code today means the server already changed the cart.
    if (checkoutErrorDetails(err)) {
      dispatch(getCart());
    }
    return;
  }

  if (err.indexOf('Your total has been updated') > -1) {
    dispatch(getCart());
  }
  if (
    err.indexOf('One or more products in your cart are no longer available') >
    -1
  ) {
    dispatch(getCart());
    dispatch(fixUnavailableCheckout());
  }
};

export const checkout =
  ({
    checkout,
    isEmbeddedMode,
    isGuest,
    cartLineItems,
    moeTrack,
    navigate,
    onSuccess,
    isStackAdaptPixelEnabled,
  }: {
    cartLineItems: CartLineItems;
    checkout: CheckoutRequest;
    isEmbeddedMode: boolean;
    isGuest: boolean;
    isStackAdaptPixelEnabled: boolean;
    moeTrack: (event: MoeEvent) => void;
    navigate: NavigateFunction;
    onSuccess: () => void;
  }): CustomerThunkAction =>
  (dispatch) => {
    dispatch(initiateCheckout());

    CartSource.save(checkout)
      .then(
        (result: CheckoutResponse) => {
          onSuccess();

          dispatch(
            checkoutSuccess({
              cartLineItems,
              isEmbeddedMode,
              isGuest,
              moeTrack,
              navigate,
              result,
              paymentMethod: checkout.payment_method,
              isStackAdaptPixelEnabled,
            })
          );
        },
        (err) => {
          dispatch(checkoutError(err));
          handleChangedCartError(err, dispatch);
        }
      )
      .catch((err) => {
        dispatch(checkoutError(err));
      });
  };

const checkoutSuccess =
  ({
    result: { message, reservation, contact, warning_message },
    isEmbeddedMode,
    cartLineItems,
    isGuest,
    moeTrack,
    navigate,
    paymentMethod,
    isStackAdaptPixelEnabled,
  }: {
    cartLineItems: CartLineItems;
    isEmbeddedMode: boolean;
    isGuest: boolean;
    isStackAdaptPixelEnabled: boolean;
    moeTrack: (event: MoeEvent) => void;
    navigate: NavigateFunction;
    paymentMethod?: string;
    result: CheckoutResponse;
  }): CustomerThunkAction =>
  (dispatch, getState, extraArgsRef) => {
    const {
      cart: { cart, deliveryAddress },
      customer,
      store,
    } = getState();
    const { uuid } = cart;
    trackBranchCheckout(uuid, trackBranchIoError);

    const { firstName, lastName, phone, email, birth_date } = customer;
    const total = Number(reservation.amount);

    const tz = store.store?.time_zone_identifier ?? shopperTimeZone();
    const delivery_finished_at = reservation.delivery_finished_at
      ? formatTime({
          time: reservation.delivery_finished_at,
          outputFormat: '',
          inputFormat: 't',
          timezone: tz,
        })
      : undefined;
    const delivery_started_at = reservation.delivery_started_at
      ? formatTime({
          time: reservation.delivery_started_at,
          outputFormat: '',
          inputFormat: 't',
          timezone: tz,
        })
      : undefined;

    postCheckoutAnalyticsEvent({
      cart: {
        ...cart,
        tags: getLocalTags(
          extraArgsRef?.current?.appMode,
          getState().embeddedApp.partnerId
        ),
        delivery_finished_at,
        delivery_started_at,
        message: reservation.message,
        payment_method: reservation.payment_method,
      },
      cartLineItems: {
        ...cartLineItems,
        total,
      },
      isStackAdaptPixelEnabled,
      deliveryAddress,
      orderCustomer: {
        birthDate: birth_date,
        email,
        firstName,
        lastName,
        phone,
      },
    });

    moeTrack(
      moeCheckout({
        cart,
        janePayStore: store.store
          ? !!store.store.jane_pay_integration?.enabled
          : false,
        storeId: store.store?.id ?? null,
        storeName: store.store?.name ?? null,
        paymentType: reservation.payment_method,
        reservationTime: delivery_started_at
          ? new Date(delivery_started_at)
          : undefined,
      })
    );

    trackCheckoutSuccess({
      cart,
      paymentMethod,
      store: store.store || undefined,
    });

    if (!warning_message) {
      // TODO: Does this notification even show up?
      NotificationsService.success(message);
    }

    if (isGuest) {
      const { is_registered } = contact || {};
      navigate(
        paths.guestReservationReceipt(reservation.uuid, {
          is_registered,
        })
      );
    } else {
      const urlQuery: Record<string, unknown> = {};
      if (warning_message) {
        urlQuery['warning_message'] = warning_message;
      }
      const path = isEmbeddedMode
        ? paths.embeddedReservation(reservation.id, urlQuery)
        : paths.reservation(reservation.id, urlQuery);
      navigate(path);
    }

    dispatch(checkoutSucceeded(reservation));
    // A v3 checkout path must reset it too.
    resetBagPickupMode();
  };

export const updateCart =
  (
    {
      product_id,
      count,
      price_id,
    }:
      | PendingCartProduct
      | {
          count: number;
          price_id: PriceId;
          product_id: number;
        },
    moeTrack: (event: MoeEvent) => void
  ): CustomerThunkAction =>
  (dispatch, getState) => {
    const { cart: cartState, bundlePossibilities, store } = getState();

    const { cart } = cartState;

    const cartProduct = cart.products.find(
      (product) =>
        product.id === product_id || product.product_id === product_id
    );
    const changeInCount = (count ?? 0) - (cartProduct?.count ?? 0);

    CartProductsSource.update(product_id, count, price_id, cart.uuid)
      .then((result) => {
        if (changeInCount > 0) {
          moeTrack(
            moeAddToBag({
              cartUuid: cart.uuid,
              product: { ...cartProduct!, count: changeInCount },
              storeId: store.store?.id ?? null,
              storeName: store.store?.name ?? null,
            })
          );
        }

        dispatch(cartUpdateSuccess(result.cart_product));

        if (bundlePossibilities.showingBundlePossibilities) {
          dispatch(setTouchedProduct(product_id));
        }
        // NOTE: Cart object gets refetched in this call to analyze specials,
        // but does not replace the entire cart object - see analyzeSpecialsSuccess.type
        // case in the reducer below.
        dispatch(
          analyzeSpecials({
            promoCode: cart.promo_code,
            reservationMode: cart.reservation_mode,
            groupSpecialId: getGroupSpecialSelection(cart.uuid) ?? undefined,
          })
        );

        // we also need to refresh the pos cart calculation
        dispatch(getPosCart());
      })
      .catch((err) => {
        const action = matchError(err);
        dispatch(action);
      });
  };

export const addToCart =
  (
    storeProduct: StoreAndCartProduct,
    moeTrack: (event: MoeEvent) => void,
    // Required, not optional: forgetting it would silently stop opening the
    // drawer rather than fail to compile.
    onAdded: () => void
  ): CustomerThunkAction<Promise<unknown>> =>
  (dispatch, getState, extraArgsRef) => {
    const appMode = extraArgsRef?.current?.appMode;
    const { cart, bundlePossibilities, application } = getState();
    return CartProductsSource.add({
      appMode,
      appModeName: appMode,
      cart_uuid: cart.cart.uuid,
      cartProduct: storeProduct.product,
      janeDeviceId: application.janeDeviceId!,
      store: storeProduct.store,
      tags: getLocalTags(appMode, getState().embeddedApp.partnerId),
    })
      .then((result) => {
        moeTrack(
          moeAddToBag({
            cartUuid: result.cart_uuid,
            product: storeProduct.product,
            storeId: storeProduct.store.id,
            storeName: storeProduct.store.name,
          })
        );

        dispatch(
          cartAddSuccess({
            ...storeProduct,
            cartUuid: result.cart_uuid,
            userAgent: navigator.userAgent,
          })
        );

        // we also need to refresh the pos cart calculation
        dispatch(getPosCart());

        if (bundlePossibilities.showingBundlePossibilities) {
          dispatch(setTouchedProduct(storeProduct.product.id));
        }

        dispatch(getCart(true));

        onAdded();
      })
      .catch((err) => {
        const action = matchError(err);
        dispatch(action);
      });
  };

export const attemptCreateHeadlessCart =
  ({
    storeId,
    products,
    reservationMode,
    externalSource,
    headlessCheckoutPartnerId,
    loginURL,
    disableGuestCheckout,
  }: {
    disableGuestCheckout?: boolean;
    externalSource?: string;
    headlessCheckoutPartnerId?: string;
    loginURL?: string;
    products: HeadlessCartProduct[];
    reservationMode: 'pickup' | 'delivery';
    storeId: string;
  }): CustomerThunkAction =>
  async (dispatch, getState, extraArgsRef) => {
    const appMode = extraArgsRef?.current?.appMode;
    const { application } = getState();

    dispatch(initiateCreateCart());

    const body = {
      app_mode: appMode,
      app_mode_name: appMode,
      disable_guest_checkout: disableGuestCheckout,
      external_source: externalSource,
      headless_checkout_partner_id: headlessCheckoutPartnerId,
      login_url: loginURL,
      products,
      reservation_mode: reservationMode,
      store_id: storeId,
      user_agent: navigator.userAgent,
      j_device_id: application?.janeDeviceId,
    };

    CartSource.create(body).then((result: { cart: Cart }) => {
      dispatch(createHeadlessCartSuccess(result));
    });
  };

export interface AttemptToAddToCartPayload extends Pick<
  AddedOrUpdatedProductInCart,
  'columnPosition' | 'location' | 'productLocation' | 'rowPosition'
> {
  almostGone?: AlmostGone;
  count: number;
  menuProduct: MenuProduct;
  moeTrack: (event: MoeEvent) => void;
  navigate: NavigateFunction;
  /**
   * Fires after a successful add. The facade opens the cart drawer here, and
   * supplies it for every consumer — hence required, not optional.
   */
  onAdded: () => void;
  price_id: PriceId;
  source: string;
  special?: StoreSpecial;
  store: DeepReadonly<Store>;
  userPreferences: UserPreferencesType;
  userSegments?: UserSegmentIds;
  withoutDrawer?: boolean;
}

export const attemptToAddToCart =
  ({
    almostGone,
    columnPosition,
    count,
    location,
    menuProduct,
    moeTrack,
    navigate,
    price_id,
    productLocation,
    rowPosition,
    source,
    special,
    store,
    userPreferences,
    onAdded,
    userSegments,
  }: AttemptToAddToCartPayload): CustomerThunkAction =>
  (dispatch, getState, extraArgsRef) => {
    const appMode = extraArgsRef?.current?.appMode;
    const {
      cart: { cart },
    } = getState();

    if (cart.store && cart.store.id !== store.id) {
      return maybeChangeStore(
        appMode,
        dispatch,
        toReplacementCart({ count, menuProduct, price_id, store }),
        navigate
      );
    }

    const productInCart = isProductInCart(cart, menuProduct.id, price_id);
    if (productInCart) {
      trackCartEvent({
        count,
        event: EventNames.UpdatedProductInCart,
        location,
        menuProduct,
        price_id,
        productLocation,
        source,
        special,
        store,
        userPreferences,
        userSegments,
      });
      dispatch(
        updateCart(
          {
            count,
            price_id,
            product_id: menuProduct.id,
          },
          moeTrack
        )
      );
    } else {
      const basePrice = Number(menuProduct[`price_${price_id}`]);
      const discountedPrice = Number(
        menuProduct[`discounted_price_${price_id}`]
      );
      postAnalyticsEvent({
        name: 'cartItemAdd',
        properties: {
          product: {
            price_id,
            ...pick(menuProduct, [
              'product_id',
              'name',
              'brand',
              'category',
              'kind',
            ]),
          },
          productId: menuProduct.product_id || menuProduct.id,
          quantity: count,
          ...(basePrice > 0 && { price: basePrice }),
          ...(discountedPrice > 0 && { discountedPrice }),
        },
      });

      trackCartEvent({
        almostGone,
        cartId: cart.id,
        columnPosition,
        count,
        event: EventNames.AddedProductToCart,
        location,
        menuProduct,
        price_id,
        productLocation,
        rowPosition,
        source,
        special,
        store,
        userPreferences,
        userSegments,
      });
      dispatch(initiateAddingItemToCart());
      dispatch(
        addToCart(
          prepareCartProduct(store, menuProduct, price_id, count),
          moeTrack,
          onAdded
        )
      );
    }
  };

export const maybeChangeStore = (
  appMode: AppMode,
  dispatch: AppDispatch,
  replacementCart: ReplacementCart,
  navigate: NavigateFunction
) => {
  setReplacementCart(replacementCart);

  if (appMode === 'default') {
    return dispatch(openModal({ name: 'replaceCart' }));
  }
  return navigate(paths.embeddedReplaceCart());
};

const isProductInCart = (
  cart: DeepReadonly<Cart>,
  productId: string | number,
  price_id: PriceId
) =>
  cart.products.find(
    (product) => product.id === productId && product.price_id === price_id
  );

const matchError = (error: string): CartActions => {
  switch (error) {
    case 'Cart is not pending':
      return initiateClearCart();
  }

  return addingItemToCartFailed();
};

export interface CartUpdatePayload {
  count: number;
  price_id: PriceId;
  product_id: number;
}

export interface CartAddPayload {
  cartUuid: string;
  product: DeepReadonly<PendingCartProduct>;
  store: DeepReadonly<Store>;
  userAgent: string;
}

interface CartDeleteItemPayload {
  priceId: PriceId;
  productId: string | number;
}

export interface CartUpdateDeliveryAddressPayload {
  deliveryAddress: Address;
}

type StoreAndCartProduct = DeepReadonly<{
  product: PendingCartProduct;
  store: Store;
}>;

export type CartState = DeepReadonly<{
  analyzingSpecials: boolean;
  cart: Cart & { status: string | undefined };
  checkoutError: string | null;
  checkoutErrorDetails: CheckoutErrorDetails | null;
  deliveryAddress: Address | null;
  hasLoaded: boolean;
  isAddingItemToCart: boolean;
  isApplyingCrmRedemption: boolean;
  isChangingReservationModes: boolean;
  isCheckingOut: boolean;
  isDeletingUnavailableProducts: boolean;
  isFixingUnavailableCheckout: boolean;
  isLoading: boolean;
  isRemovingSpecialHasChangedFlag: boolean;
  salesTaxLoading: boolean;
  selectedPaymentMethod: string | null;
  /** iso8601 datetime */
  wasLoadedAt: string | undefined;
}>;

const getDefaultCart = (): CartState['cart'] => ({
  all_discounts_total: 0.0,
  brand_discounts: [],
  brand_discounts_service_fee_total: 0.0,
  brand_discounts_total: 0.0,
  brand_discounts_total_with_fee: 0.0,
  brand_special_has_changed: false,
  cart_discount_amount: 0.0,
  cart_discount_special_id: undefined,
  crm_redemptions: [],
  delivery_fee_amount: 0.0,
  discounted_product_total: 0.0,
  discounted_subtotal: 0.0,
  group_discounts_total: 0.0,
  group_special_id: null,
  group_special_title: null,
  item_total: 0.0,
  jane_pay_service_fee_amount: 0.0,
  loyalty_points_stacking_enabled: true,
  max_specials_per_cart: null,
  max_specials_per_cart_hit: false,
  order_total: 0.0,
  product_discounts_total: 0.0,
  products: [],
  removed_crm_redemptions: [],
  reservation_mode: 'pickup',
  rounding_amount: 0.0,
  sales_tax_amount: 0.0,
  sales_tax_rate: 0.0,
  service_fee_amount: 0.0,
  special_has_changed: false,
  status: undefined,
  store: undefined,
  store_tax_amount: 0.0,
  tax_included: false,
  tip_amount: '0.0',
  unavailable_products: [],
  uuid: '',
});

const getInitialState = (): CartState => ({
  analyzingSpecials: false,
  cart: getDefaultCart(),
  checkoutError: null,
  checkoutErrorDetails: null,
  deliveryAddress: Storage.get('deliveryAddress') || null,
  hasLoaded: false,
  isAddingItemToCart: false,
  isApplyingCrmRedemption: false,
  isChangingReservationModes: false,
  isCheckingOut: false,
  isDeletingUnavailableProducts: false,
  isFixingUnavailableCheckout: false,
  isLoading: false,
  isRemovingSpecialHasChangedFlag: false,
  salesTaxLoading: false,
  selectedPaymentMethod: null,
  wasLoadedAt: undefined,
});

const clear = (state: CartState) => ({
  ...state,
  cart: getDefaultCart(),
  deliveryAddress: null,
});

const clearOnError = (state: CartState) =>
  clear({
    ...state,
    hasLoaded: true,
    isAddingItemToCart: false,
    isLoading: false,
  });

const cartSlice = createSlice({
  name: 'cart',
  initialState: getInitialState(),
  reducers: {
    checkoutCancelled: (state) => {
      return { ...state, isCheckingOut: false };
    },
    dismissCheckoutError: (state) => {
      return { ...state, checkoutError: null, checkoutErrorDetails: null };
    },
    updateTip: (state, action: PayloadAction<string>) => {
      return { ...state, cart: { ...state.cart, tip_amount: action.payload } };
    },
    updatePaymentMethod: (state, action: PayloadAction<string | null>) => {
      return { ...state, selectedPaymentMethod: action.payload };
    },
    initiateAddingItemToCart: (state) => {
      return { ...state, isAddingItemToCart: true };
    },
    addingItemToCartFailed: (state) => {
      return { ...state, isAddingItemToCart: false };
    },
    cartAddSuccess: (state, action: PayloadAction<CartAddPayload>) => {
      const { store, product, userAgent, cartUuid } = action.payload;
      return {
        ...state,
        cart: {
          ...state.cart,
          products: [...state.cart.products, product],
          status: 'pending',
          store,
          user_agent: userAgent,
          uuid: cartUuid,
        },
        isAddingItemToCart: false,
      };
    },
    getCartError: (state, _action: PayloadAction<unknown>) =>
      clearOnError(state),
    clearCartError: clearOnError,
    initiateClearCart: clearOnError,
    cartUpdateSuccess: (
      state,
      action: PayloadAction<PendingCartProduct | CartUpdatePayload>
    ) => {
      const { cart } = state;
      const updatedProduct = action.payload;

      const products = cart.products.map((product) =>
        product.id === updatedProduct.product_id &&
        product.price_id === updatedProduct.price_id
          ? {
              ...product,
              count: updatedProduct.count,
            }
          : product
      );
      return {
        ...state,
        cart: {
          ...cart,
          products,
        },
      };
    },
    initiateCheckout: (state) => {
      return { ...state, isCheckingOut: true };
    },
    checkoutSucceeded: (
      state,
      _action: PayloadAction<CheckoutResponse['reservation']>
    ) => {
      return { ...clear(state), hasLoaded: true, isCheckingOut: false };
    },
    checkoutError: (state, action: PayloadAction<unknown>) => {
      const details = checkoutErrorDetails(action.payload);

      return {
        ...state,
        // CartAlerts renders a structured refusal; the banner would repeat it.
        checkoutError: details
          ? null
          : typeof action.payload === 'string'
            ? action.payload
            : 'Something went wrong, please try again. If the problem persists, please contact us at support@iheartjane.com',
        checkoutErrorDetails: details ?? null,
        isCheckingOut: false,
      };
    },
    initiateCreateCart: (state) => {
      return { ...state, isLoading: true };
    },
    initiateGetCart: (state) => {
      return { ...state, isLoading: true };
    },
    getRemoteCartSucceeded: (state) => {
      return {
        ...state,
        wasLoadedAt: new Date().toISOString(),
      };
    },
    getUserCartSucceeded: (
      state,
      action: PayloadAction<{ cart: DeepReadonly<Cart> }>
    ) => {
      const { cart } = state;
      /**
       * the cart/get_for_user endpoint doesn't know the user address
       * since it can be called from any point of the app after log in
       * so we won't overwrite any work done on the cart that may have
       * updated its address/delivery_fee since that's the last true value
       * we have to do the same for the order total, since the one from the
       * get_for_user endpoint won't add the delivery fee either
       */
      return {
        ...state,
        cart: {
          ...getDefaultCart(),
          ...action.payload.cart,
          delivery_fee_amount: cart.delivery_fee_amount || 0,
          order_total: cart.order_total,
          reservation_mode: cart.reservation_mode,
        },
        hasLoaded: true,
        isChangingReservationModes: false,
        isLoading: false,
      };
    },
    getCartSucceeded: (
      state,
      action: PayloadAction<{ cart: DeepReadonly<Cart> }>
    ) => {
      return {
        ...state,
        cart: {
          ...getDefaultCart(),
          ...action.payload.cart,
        },
        hasLoaded: true,
        isChangingReservationModes: false,
        isLoading: false,
      };
    },
    refreshCartSuccess: (state, action: PayloadAction<{ cart: Cart }>) => {
      return {
        ...state,
        cart: {
          ...getDefaultCart(),
          ...action.payload.cart,
        },
        hasLoaded: true,
        isLoading: false,
      };
    },
    createHeadlessCartSuccess: (
      state,
      action: PayloadAction<{ cart: Cart }>
    ) => {
      return {
        ...state,
        cart: {
          ...getDefaultCart(),
          ...action.payload.cart,
        },
        hasLoaded: true,
        isLoading: false,
      };
    },
    initiateAnalyzeSpecials: (state) => {
      return { ...state, analyzingSpecials: true };
    },
    analyzeSpecialsSuccess: (state, action: PayloadAction<{ cart: Cart }>) => {
      const { cart } = action.payload;

      // use only amounts, not the whole cart object, until the cart optimizer stops interpreting product price_id changes as special_has_changed
      const {
        all_discounts_total,
        brand_discounts,
        brand_discounts_service_fee_total,
        brand_discounts_total,
        brand_discounts_total_with_fee,
        brand_special_has_changed,
        cart_discount_amount,
        cart_discount_special_id,
        crm_redemptions,
        delivery_fee_amount,
        discounted_product_total,
        discounted_subtotal,
        group_discounts_total,
        group_special_id,
        group_special_title,
        item_total,
        jane_pay_service_fee_amount,
        order_total,
        product_discounts_total,
        products,
        promo_code,
        removed_crm_redemptions,
        rounding_amount,
        sales_tax_amount,
        sales_tax_rate,
        service_fee_amount,
        store_tax_amount,
        unavailable_products,
      } = cart;

      return {
        ...state,
        analyzingSpecials: false,
        cart: {
          ...state.cart,
          all_discounts_total,
          brand_discounts,
          brand_discounts_service_fee_total,
          brand_discounts_total,
          brand_discounts_total_with_fee,
          brand_special_has_changed,
          cart_discount_amount,
          cart_discount_special_id,
          crm_redemptions,
          delivery_fee_amount,
          discounted_product_total,
          discounted_subtotal,
          group_discounts_total,
          group_special_id,
          group_special_title,
          item_total,
          jane_pay_service_fee_amount,
          order_total,
          product_discounts_total,
          products,
          promo_code,
          removed_crm_redemptions,
          rounding_amount,
          sales_tax_amount,
          sales_tax_rate,
          service_fee_amount,
          store_tax_amount,
          unavailable_products,
        },
      };
    },
    initiateApplyCrmRedemption: (state) => {
      return {
        ...state,
        isApplyingCrmRedemption: true,
      };
    },
    applyCrmRedemptionError: (state) => {
      return {
        ...state,
        isApplyingCrmRedemption: false,
      };
    },
    updateCrmRedemptions: (state, action: PayloadAction<{ cart: Cart }>) => {
      return {
        ...state,
        cart: {
          ...state.cart,
          ...action.payload.cart,
        },
        isApplyingCrmRedemption: false,
      };
    },
    removeCrmRedemptionSuccess: (
      state,
      action: PayloadAction<{ id: string | number }>
    ) => {
      const { id } = action.payload;
      return {
        ...state,
        cart: {
          ...state.cart,
          crm_redemptions: state.cart.crm_redemptions?.filter(
            (redemption) => redemption.id !== id
          ),
        },
      };
    },
    clearRemovedCrmRedemptions: (state) => {
      return {
        ...state,
        cart: {
          ...state.cart,
          removed_crm_redemptions: [],
        },
      };
    },
    cartDeleteItemSuccess: (
      state,
      action: PayloadAction<CartDeleteItemPayload>
    ) => {
      if (
        state.cart.products.length === 1 &&
        state.cart.products[0].id === action.payload.productId
      ) {
        return clear(state);
      }

      return {
        ...state,
        cart: {
          ...state.cart,
          products: state.cart.products.filter(
            (product) =>
              !(
                product.id === action.payload.productId &&
                product.price_id === action.payload.priceId
              )
          ),
        },
      };
    },
    fixUnavailableCheckout: (state) => {
      return { ...state, isFixingUnavailableCheckout: true };
    },
    unavailableCheckoutFixed: (state) => {
      return { ...state, isFixingUnavailableCheckout: false };
    },
    initiateDeleteUnavailableProducts: (state) => {
      return { ...state, isDeletingUnavailableProducts: true };
    },
    deleteUnavailableProductsSuccess: (
      state,
      _action: PayloadAction<(string | number)[]>
    ) => {
      const newState = {
        ...state,
        cart: { ...state.cart, unavailable_products: [] },
        checkoutError: null,
        checkoutErrorDetails: null,
        isDeletingUnavailableProducts: false,
        isFixingUnavailableCheckout: false,
      };

      return state.cart.products.length === 0 ? clear(newState) : newState;
    },
    initiateRemoveSpecialHasChangedFlag: (state) => {
      return { ...state, isRemovingSpecialHasChangedFlag: true };
    },
    removeSpecialHasChangedFlagSuccess: (state) => {
      return {
        ...state,
        cart: { ...state.cart, special_has_changed: false },
        isRemovingSpecialHasChangedFlag: false,
      };
    },
    removeBrandSpecialHasChangedFlag: (state) => {
      return {
        ...state,
        cart: {
          ...state.cart,
          brand_special_has_changed: false,
          brand_special_unavailable_reason: null,
        },
      };
    },
    cartUpdateReservationMode: (
      state,
      action: PayloadAction<{ reservationMode: ReservationMode }>
    ) => {
      return {
        ...state,
        isChangingReservationModes: true,
        cart: {
          ...state.cart,
          reservation_mode: action.payload.reservationMode,
        },
      };
    },
    initiateUpdateDeliveryAddress: (
      state,
      action: PayloadAction<CartUpdateDeliveryAddressPayload>
    ) => {
      return {
        ...state,
        deliveryAddress: action.payload.deliveryAddress,
      };
    },
    removeDeliveryAddress: (state) => {
      return {
        ...state,
        deliveryAddress: null,
      };
    },
    initiateGetLocalCartSuccess: (state) => state, // Dispatched as side-effect marker only; no state change.
  },
  selectors: {
    selectCart: (state) => state,
  },
});

export const {
  reducer: cartReducer,
  selectors: { selectCart },
} = cartSlice;

export const {
  analyzeSpecialsSuccess,
  cartAddSuccess,
  cartDeleteItemSuccess,
  cartUpdateReservationMode,
  cartUpdateSuccess,
  checkoutCancelled,
  checkoutError,
  checkoutSucceeded,
  clearCartError,
  clearRemovedCrmRedemptions,
  deleteUnavailableProductsSuccess,
  dismissCheckoutError,
  fixUnavailableCheckout,
  getCartError,
  getCartSucceeded,
  getUserCartSucceeded,
  initiateAnalyzeSpecials,
  initiateCheckout,
  initiateClearCart,
  initiateCreateCart,
  initiateDeleteUnavailableProducts,
  initiateGetCart,
  initiateGetLocalCartSuccess,
  initiateRemoveSpecialHasChangedFlag,
  initiateUpdateDeliveryAddress,
  removeBrandSpecialHasChangedFlag,
  removeCrmRedemptionSuccess,
  removeDeliveryAddress,
  removeSpecialHasChangedFlagSuccess,
  unavailableCheckoutFixed,
  updateCrmRedemptions,
  updatePaymentMethod,
  updateTip,
} = cartSlice.actions;

const {
  addingItemToCartFailed,
  applyCrmRedemptionError,
  createHeadlessCartSuccess,
  getRemoteCartSucceeded,
  initiateAddingItemToCart,
  initiateApplyCrmRedemption,
  refreshCartSuccess,
} = cartSlice.actions;

export const persistCartToLocalStorage = (
  store: ReturnType<typeof configureCustomerStore>,
  appMode: AppMode
) => {
  let lastCart: CartState['cart'] | null = null;
  let lastDeliveryAddress: CartState['deliveryAddress'] | null = null;

  store.subscribe(() => {
    const {
      cart: { cart, deliveryAddress },
      embeddedApp: { partnerId },
    } = store.getState();

    const cartKey = cartStorageKey(appMode, partnerId);

    const persist = (cart: CartState['cart']) => {
      if (lastCart === cart) return;

      lastCart = cart;

      const localCart = {
        products: cart.products.map((product) => ({
          count: product.count,
          id: product.id,
          price_id: product.price_id,
        })),
        reservation_mode: cart.reservation_mode,
        status: cart.status,
        store: cart.store && pick(cart.store, ['id', 'name']),
        uuid: cart.uuid,
      };

      Storage.set(cartKey, localCart);
    };

    if (lastDeliveryAddress !== deliveryAddress) {
      lastDeliveryAddress = deliveryAddress;
      Storage.set('deliveryAddress', deliveryAddress);
    }

    if (cart.status || lastCart) {
      persist(cart);
    }
  });
};

export type CartActions =
  | ReturnType<typeof initiateAddingItemToCart>
  | ReturnType<typeof addingItemToCartFailed>
  | ReturnType<typeof initiateCreateCart>
  | ReturnType<typeof initiateGetCart>
  | ReturnType<typeof getRemoteCartSucceeded>
  | ReturnType<typeof initiateClearCart>
  | ReturnType<typeof clearCartError>
  | ReturnType<typeof cartDeleteItemSuccess>
  | ReturnType<typeof updateTip>
  | ReturnType<typeof updatePaymentMethod>
  | ReturnType<typeof initiateRemoveSpecialHasChangedFlag>
  | ReturnType<typeof initiateDeleteUnavailableProducts>
  | ReturnType<typeof dismissCheckoutError>
  | ReturnType<typeof cartUpdateReservationMode>
  | ReturnType<typeof initiateAnalyzeSpecials>
  | ReturnType<typeof initiateApplyCrmRedemption>
  | ReturnType<typeof applyCrmRedemptionError>
  | ReturnType<typeof updateCrmRedemptions>
  | ReturnType<typeof removeCrmRedemptionSuccess>
  | ReturnType<typeof fixUnavailableCheckout>
  | ReturnType<typeof unavailableCheckoutFixed>
  | ReturnType<typeof initiateCheckout>
  | ReturnType<typeof cartUpdateSuccess>
  | ReturnType<typeof cartAddSuccess>
  | ReturnType<typeof initiateGetLocalCartSuccess>
  | ReturnType<typeof getCartSucceeded>
  | ReturnType<typeof getUserCartSucceeded>
  | ReturnType<typeof refreshCartSuccess>
  | ReturnType<typeof checkoutCancelled>
  | ReturnType<typeof checkoutSucceeded>
  | ReturnType<typeof checkoutError>
  | ReturnType<typeof analyzeSpecialsSuccess>
  | ReturnType<typeof getCartError>
  | ReturnType<typeof deleteUnavailableProductsSuccess>
  | ReturnType<typeof clearRemovedCrmRedemptions>
  | ReturnType<typeof removeSpecialHasChangedFlagSuccess>
  | ReturnType<typeof removeBrandSpecialHasChangedFlag>
  | ReturnType<typeof createHeadlessCartSuccess>
  | ReturnType<typeof initiateUpdateDeliveryAddress>
  | ReturnType<typeof removeDeliveryAddress>;
